import { useState } from "react";
import { useLocation } from "react-router-dom";

import { NetworkStatus, useLazyQuery } from "@apollo/client";

import { useDateRange } from "../../../../common/components/datePicker/modelsDatePickers";
import { useCustomerIdGuard } from "../../../../common/hooks/useCustomerIdGuard";
import { RouteEnum } from "../../../../common/models/enums";
import { EventsApiResponse } from "../../../../common/types/events/api";
import { GetRecentEventsParams } from "../../../../common/types/events/params";
import { buildEventQueryVariables } from "../../../../common/utils/buildEventQueryVariables";
import { handleApolloError } from "../../../../common/utils/handleApolloError";
import { errorNotification } from "../../../../common/variables/notification";
import {
  useSelectedLocation,
  useSelectedTagLocationIds,
} from "../../../../common/variables/selectedLocation";
import { useSelectedModel } from "../../../../common/variables/selectedModel";
import { GET_RECENT_EVENTS } from "../../../../rest/queries";
import { useSelectedDataHubCamera } from "../../../data-hub/components/dataHub.variable";
import { mapEventWithRecording } from "../../adapters/eventWithRecordingAdapter";
import { EventWithRecordingViewModel } from "../../models/eventWithRecordingViewModel";
import {
  DEFAULT_EXPORT_PAGE_SIZE,
  getDefaultFromDate,
  normalizeDeviceId,
  normalizeLocationId,
} from "./exportFilters";

export const HARDHAT_SELECT_OPTION = { title: "Hard hat", value: "HARDHAT" };

export const useGetEventsToExport = () => {
  const { pathname } = useLocation();

  const customerId = useCustomerIdGuard();
  const facilityId = useSelectedLocation();
  const tagLocationIds = useSelectedTagLocationIds();
  const dateRange = useDateRange();
  let modelId = useSelectedModel();
  const camera = useSelectedDataHubCamera();

  if (
    pathname === RouteEnum.TelemetryHardHat ||
    pathname === RouteEnum.SensoryTelemetryHardHat ||
    pathname === RouteEnum.HardHat ||
    pathname === RouteEnum.SensoryHardHat
  ) {
    modelId = HARDHAT_SELECT_OPTION;
  }

  // Match EventTableContainer: when a tag filter is active, don't scope the
  // API by facilityId — narrow client-side instead so the export aligns with
  // what's rendered in the table.
  const shouldUseTagFilter = !!(tagLocationIds && tagLocationIds.length > 0);

  const params: GetRecentEventsParams = {
    customerId,
    modelId: modelId?.value ?? "",
    facilityId: shouldUseTagFilter ? "" : facilityId?.value ?? "",
    fromDate: dateRange?.from
      ? dateRange.from.toISOString()
      : getDefaultFromDate(),
    toDate: dateRange?.to
      ? dateRange.to.toISOString()
      : new Date().toISOString(),
    pageSize: DEFAULT_EXPORT_PAGE_SIZE,
  };

  const variables = buildEventQueryVariables(params);

  const [loading, setLoading] = useState(false);

  const [fetchEvents, { error, networkStatus }] = useLazyQuery<{
    events: EventsApiResponse;
  }>(GET_RECENT_EVENTS, {
    variables,
    fetchPolicy: "no-cache",
    onError: handleApolloError,
  });

  const getEvents = async (): Promise<EventWithRecordingViewModel[]> => {
    setLoading(true);

    try {
      const initialResponse = await fetchEvents({
        variables: {
          ...variables,
          lastEvaluatedKey: "",
        },
      });

      let allEvents = initialResponse.data?.events?.data ?? [];

      let currentLastEvaluatedKey =
        initialResponse.data?.events?.lastEvaluatedKey;

      while (currentLastEvaluatedKey) {
        const moreResponse = await fetchEvents({
          variables: {
            ...variables,
            pageSize: DEFAULT_EXPORT_PAGE_SIZE,
            lastEvaluatedKey: encodeURIComponent(
              JSON.stringify(currentLastEvaluatedKey)
            ),
          },
        });

        const newEvents = moreResponse.data?.events?.data ?? [];

        allEvents = [...allEvents, ...newEvents];

        currentLastEvaluatedKey = moreResponse.data?.events?.lastEvaluatedKey;
      }

      let mapped = allEvents.map(mapEventWithRecording);

      const selectedCamera = normalizeDeviceId(camera?.value ?? "");

      if (selectedCamera) {
        mapped = mapped.filter(({ event, newestRecording }) => {
          const fromEvent = normalizeDeviceId(event.camera_id ?? "");
          const fromRecording = normalizeDeviceId(
            newestRecording?.rawEventInfo?.data?.camera_id ??
              newestRecording?.rawEventInfo?.data?.instance_name ??
              ""
          );

          return (
            fromEvent === selectedCamera || fromRecording === selectedCamera
          );
        });
      }

      if (shouldUseTagFilter) {
        const normalizedTagIds = tagLocationIds!.map(normalizeLocationId);

        mapped = mapped.filter(({ event }) => {
          const eventLoc = normalizeLocationId(
            event.facilityId ?? event.location ?? ""
          );

          return normalizedTagIds.includes(eventLoc);
        });
      }

      setLoading(false);

      return mapped;
    } catch (err) {
      console.error("Failed to fetch all events for export:", err);

      errorNotification("Failed to fetch events for export");

      setLoading(false);

      return [];
    }
  };

  return {
    getEvents,
    loading: loading || networkStatus === NetworkStatus.fetchMore,
    error,
  } as const;
};
